import axios from 'utils/axios';

export async function createTask(name, detail, useTeamAssignment, assignedToProviderId, dueDate, assignedToPatientId, protocolTemplateId) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/create`, {
            name,
            detail,
            useTeamAssignment,
            assignedToProviderId,
            dueDate,
            assignedToPatientId,
            protocolTemplateId
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function editTask(taskId, assignedToProviderId, dueDate) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/${taskId}/edit`, {
            assignedToProviderId,
            dueDate
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function closeTask(taskId, closeNote) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/${taskId}/close`, {
            closeNote
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function bulkCloseTasks(taskIds, closeNote) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/bulk-close`, {
            taskIds,
            closeNote
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function bulkReassign(taskIds, assignedToProviderId) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/bulk-reassign`, {
            taskIds,
            assignedToProviderId
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function bulkChangeDate(taskIds, dueDate) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/bulk-change-date`, {
            taskIds,
            dueDate
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function bulkCreate(name, detail, useTeamAssignment, assignedToProviderId, dueDate, patientPopulation, protocolTemplateId) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/bulk-create`, {
            name,
            detail,
            useTeamAssignment,
            assignedToProviderId,
            dueDate,
            patientPopulation,
            protocolTemplateId
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

// filters: the chip array from views/task/filters/taskFilterDefinitions ({ field, operator, value }),
// sent as one JSON query parameter; the server resolves relative dates and the MTD month in the app zone
export async function getTaskList({ filters, sortField, sortDirection }, patientId, pageNumber, pageSize) {
    try {
        const params = {
            pageSize,
            pageNumber,
            sortField,
            sortDirection
        };
        if (filters && filters.length > 0) {
            params.filters = JSON.stringify(filters);
        }
        if (patientId) {
            params.patientId = patientId;
        }

        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/list`, {
            params
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

// "due today" is resolved on the server in the app zone, the same rule as the task list's "Due date is on or before today" chip
export async function getActiveTaskStatsForLoggedInUser(clinicId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/task-stats/active-tasks-logged-in-user`);

        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function getTaskStatsForHomepage(clinicId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/task-stats/homepage`);
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function getTaskComments(taskId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/${taskId}/comments`);
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function createTaskComment(taskId, comment) {
    try {
        const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/${taskId}/comment`, {
            comment
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}

export async function getTaskById(taskId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/${taskId}`);
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            throw e;
        }
        throw new Error('unknown error');
    }
}

// SYN-87 phase 2: a sentence typed into the Filter menu, turned into chips by the
// model. Nothing is applied by the server; the caller previews and applies.
// Response: { filters: [{ field, operator, value, sourcePhrase, bestGuess }], unmatched: [phrase] }.
// Rejects with the server error body ({ statusCode, message }).
export async function suggestTaskFilters({ text, currentFilters, lockedToPatient }) {
    const response = await axios.post(`/api/org-id/{orgId}/clinic-id/{clinicId}/task/list/ai-filter`, {
        text,
        currentFilters,
        lockedToPatient
    });
    return response.data;
}
